import { useState } from "react";
import {
  DOMAIN_LABELS,
  automaticDirtyDomains,
  domainChipLabel,
  operatorDirtyDomains,
  shouldShowSyncChanges,
  waitingToSyncCopy,
} from "@/lib/phase1-sync-contract";
import { syncListingDomains } from "@/lib/api/listing-sync";

export function CatalogueSyncPanel(props: {
  orgId: string;
  productId: string;
  listings: Array<{
    id: string;
    connectorName: string;
    provider: string | null;
    dirty: string[] | null;
    publicationStatus: string | null;
  }>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [preflight, setPreflight] = useState<string>("");
  const operatorDirty = props.listings.flatMap((l) => operatorDirtyDomains(l.dirty));
  const autoDirty = props.listings.flatMap((l) => automaticDirtyDomains(l.dirty));
  const showSync = shouldShowSyncChanges(operatorDirty);

  const run = async (dryRun: boolean) => {
    setBusy(true);
    setMessage(dryRun ? "Syncing..." : "Syncing...");
    const domains = [...new Set(operatorDirty.length ? operatorDirty : ["content", "media", "seo"])];
    const result = await syncListingDomains({
      orgId: props.orgId,
      productId: props.productId,
      domains,
      dryRun,
    });
    setBusy(false);
    if (!result.success && !result.data?.results) {
      setMessage(result.error || "Sync failed — Retry");
      return;
    }
    const lines = (result.data?.results ?? []).map((row) => {
      const parts = Object.entries(row.domains || {}).map(([domain, info]) => {
        const label = DOMAIN_LABELS[domain] ?? domain;
        if (info.status === "synced" || info.status === "ready") return `${label} ✓`;
        if (info.status === "blocked") return `${label} blocked${info.reason ? ` — ${info.reason}` : ""}`;
        if (info.status === "failed") return `${label} failed${info.reason ? ` — ${info.reason}` : ""}`;
        if (info.status === "skipped_override") return `${label} override preserved`;
        return `${label} ${info.status}`;
      });
      return `${row.provider}: ${parts.join(" · ")}`;
    });
    setPreflight(lines.join("\n"));
    setMessage(dryRun ? "Preflight complete" : result.success ? "Synced" : "Sync failed — Retry");
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-800">Channel sync</h3>
        {showSync && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(false)}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg"
          >
            {busy ? "Syncing..." : "Sync changes"}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500">Price and inventory sync automatically after canonical changes. Content, media, SEO and mapping wait for Sync changes.</p>
      {autoDirty.length > 0 && (
        <p className="text-xs text-slate-600">{[...new Set(autoDirty)].map((d) => domainChipLabel(d, "needs_sync")).join(" · ")}</p>
      )}
      {showSync && <p className="text-xs text-amber-700">{waitingToSyncCopy(operatorDirty)}</p>}
      {props.listings.map((l) => (
        <div key={l.id} className="text-xs text-slate-600 flex flex-wrap gap-2">
          <span className="font-medium text-slate-800">{l.connectorName}</span>
          {(l.dirty ?? []).length === 0 ? (
            <span className="text-emerald-700">Synced</span>
          ) : (
            (l.dirty ?? []).map((d) => (
              <span key={d} className="px-1.5 py-0.5 rounded-full bg-slate-100">{domainChipLabel(d, automaticDirtyDomains([d]).length ? "needs_sync" : "needs_sync")}</span>
            ))
          )}
        </div>
      ))}
      {showSync && (
        <button type="button" disabled={busy} onClick={() => void run(true)} className="text-xs font-medium text-emerald-700">
          Preview preflight
        </button>
      )}
      {message && <p className="text-xs text-slate-700">{message}</p>}
      {preflight && <pre className="text-xs bg-slate-50 border border-slate-100 rounded-lg p-3 whitespace-pre-wrap">{preflight}</pre>}
    </div>
  );
}
